// accesscontrolmanagement.js - Fixed version with Quick Actions in Grant Access tab only
import React, { useState, useEffect } from 'react';
import './accesscontrolmanagement.css';

const AccessControlManagement = () => {
  // All useState hooks declared at the top - never conditional
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedFeature, setSelectedFeature] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [studentAccessInfo, setStudentAccessInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('grant');

  const API_BASE = process.env.REACT_APP_API_BASE || 'http://80.225.197.31:8001';

  const features = [
    { value: 'teaching_mode', label: 'Teaching Mode' },
    { value: 'doubt_resolution', label: 'Doubt Resolution' },
    { value: 'quiz', label: 'Quiz' },
    { value: 'enhanced_learning', label: 'Enhanced Learning' },
    { value: 'study_material', label: 'Study Material' },
    { value: 'live_quiz', label: 'Live Quiz' }
  ];

  // useEffect hooks - always called in the same order
  useEffect(() => {
    const fetchInitialData = async () => {
      await Promise.all([fetchStudents(), fetchSubjects()]);
    };
    fetchInitialData();
  }, []); // Empty dependency array

  const fetchStudents = async () => {
    try {
      const response = await fetch(`${API_BASE}/students/`);
      if (response.ok) {
        const data = await response.json();
        setStudents(data);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      setMessage('❌ Failed to load students');
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await fetch(`${API_BASE}/subjects/`);
      if (response.ok) {
        const data = await response.json();
        setSubjects(data);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setMessage('❌ Failed to load subjects');
    }
  };

  const fetchStudentAccess = async (studentId) => {
    try {
      const response = await fetch(`${API_BASE}/unified-access/${studentId}/access-info`);
      if (response.ok) {
        const data = await response.json();
        setStudentAccessInfo(data);
      } else {
        setStudentAccessInfo(null);
      }
    } catch (error) {
      console.error('Error fetching student access:', error);
      setStudentAccessInfo(null);
      setMessage('❌ Failed to load student access info');
    }
  };

  const handleGrantAccess = async (e) => {
    e.preventDefault();
    
    if (!selectedStudent || !selectedSubject || !selectedFeature || !startDate) {
      setMessage('❌ Please fill all required fields');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE}/unified-access/grant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          student_id: parseInt(selectedStudent),
          subject_id: parseInt(selectedSubject),
          feature_name: selectedFeature,
          start_date: startDate,
          end_date: endDate || null,
          created_by: 'admin'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('✅ Access granted successfully!');
        // Reset form
        setSelectedStudent('');
        setSelectedSubject('');
        setSelectedFeature('');
        setStartDate('');
        setEndDate('');
        // Refresh student access info if viewing same student
        if (studentAccessInfo && studentAccessInfo.student_id === parseInt(selectedStudent)) {
          await fetchStudentAccess(selectedStudent);
        }
      } else {
        setMessage(`❌ Error: ${data.detail || 'Failed to grant access'}`);
      }
    } catch (error) {
      console.error('Error granting access:', error);
      setMessage('❌ Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentSelect = async (studentId) => {
    setSelectedStudent(studentId);
    if (studentId) {
      await fetchStudentAccess(studentId);
    } else {
      setStudentAccessInfo(null);
    }
  };

  return (
    <div className="access-control-management">
      <div className="header">
        <h1>🔐 Access Control Management</h1>
        <p>Manage student access to subjects and features</p>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab ${activeTab === 'grant' ? 'active' : ''}`}
          onClick={() => setActiveTab('grant')}
        >
          Grant Access
        </button>
        <button 
          className={`tab ${activeTab === 'view' ? 'active' : ''}`}
          onClick={() => setActiveTab('view')}
        >
          View & Manage
        </button>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
          {message}
          <button onClick={() => setMessage('')} className="close-message">×</button>
        </div>
      )}

      {/* Grant Access Tab */}
      {activeTab === 'grant' && (
        <div className="grant-access-section">
          <h2>Grant New Access</h2>
          <form onSubmit={handleGrantAccess} className="access-form">
            <div className="form-row">
              <div className="form-group">
                <label>Student *</label>
                <select 
                  value={selectedStudent} 
                  onChange={(e) => setSelectedStudent(e.target.value)}
                  required
                >
                  <option value="">Select Student</option>
                  {students.map(student => (
                    <option key={student.student_id} value={student.student_id}>
                      {student.name} ({student.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Subject *</label>
                <select 
                  value={selectedSubject} 
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  required
                >
                  <option value="">Select Subject</option>
                  {subjects.map(subject => (
                    <option key={subject.subject_id} value={subject.subject_id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Feature *</label>
                <select 
                  value={selectedFeature} 
                  onChange={(e) => setSelectedFeature(e.target.value)}
                  required
                >
                  <option value="">Select Feature</option>
                  {features.map(feature => (
                    <option key={feature.value} value={feature.value}>
                      {feature.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Date *</label>
                <input 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>End Date (Optional)</label>
                <input 
                  type="date" 
                  value={endDate} 
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div className="form-group">
                <button type="submit" disabled={loading} className="grant-btn">
                  {loading ? 'Granting...' : 'Grant Access'}
                </button>
              </div>
            </div>
          </form>

          {/* Quick Actions - Only shown in Grant Access tab */}
          <div className="quick-actions">
            <h3>Quick Actions</h3>
            <div className="action-buttons">
              <button onClick={async () => {
                if (!selectedStudent || !selectedSubject) {
                  setMessage('❌ Please select a student and subject first');
                  return;
                }
                
                if (window.confirm(`Grant ALL features to ${students.find(s => s.student_id === parseInt(selectedStudent))?.name} for ${subjects.find(s => s.subject_id === parseInt(selectedSubject))?.name}?`)) {
                  setLoading(true);
                  try {
                    const promises = features.map(feature => 
                      fetch(`${API_BASE}/unified-access/grant`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          student_id: parseInt(selectedStudent),
                          subject_id: parseInt(selectedSubject),
                          feature_name: feature.value,
                          start_date: new Date().toISOString().split('T')[0],
                          end_date: null,
                          created_by: 'admin'
                        })
                      })
                    );
                    
                    await Promise.all(promises);
                    setMessage('✅ All features granted successfully!');
                    
                    if (studentAccessInfo && studentAccessInfo.student_id === parseInt(selectedStudent)) {
                      await fetchStudentAccess(selectedStudent);
                    }
                  } catch (error) {
                    setMessage('❌ Failed to grant all features');
                  } finally {
                    setLoading(false);
                  }
                }
              }}>
                🚀 Grant All Features
              </button>
              
              <button onClick={async () => {
                await Promise.all([fetchStudents(), fetchSubjects()]);
                setMessage('✅ Data refreshed');
              }}>
                🔄 Refresh Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View & Manage Tab */}
      {activeTab === 'view' && (
        <div className="view-access-section">
          <h2>View & Manage Access</h2>
          
          <div className="student-selector">
            <label>Select Student to View Access:</label>
            <select 
              value={selectedStudent} 
              onChange={(e) => handleStudentSelect(e.target.value)}
            >
              <option value="">Choose Student</option>
              {students.map(student => (
                <option key={student.student_id} value={student.student_id}>
                  {student.name} ({student.email})
                </option>
              ))}
            </select>
          </div>

          {/* Student Access Information */}
          {/* Student Access Information */}
          {studentAccessInfo && (
            <div className="student-access-info">
              <h3>Access Details for {studentAccessInfo.student_name}</h3>
              <p><strong>Email:</strong> {studentAccessInfo.student_email}</p>
              <p><strong>Total Access Records:</strong> {studentAccessInfo.subjects?.length || 0}</p>

              {studentAccessInfo.subjects && studentAccessInfo.subjects.length > 0 ? (
                <div className="access-cards">
                  {studentAccessInfo.subjects.map((subject, index) => (
                    <div key={`${subject.subject_id}-${index}`} className="subject-access-card">
                      <h4>{subject.subject_name}</h4>
                      <div className="features-grid">
                        {subject.features.map((feature, featureIndex) => (
                          <div 
                            key={`${typeof feature === 'object' ? feature.name : feature}-${featureIndex}`} 
                            className={`feature-access-item ${typeof feature === 'object' && !feature.is_active ? 'revoked-access' : ''}`}
                          >
                            <span className="feature-name">
                              {features.find(f => f.value === (typeof feature === 'object' ? feature.name : feature))?.label || (typeof feature === 'object' ? feature.name : feature)}
                              {typeof feature === 'object' && !feature.is_active && <span className="revoked-indicator"> (Revoked)</span>}
                            </span>
                            <div className="action-buttons-group">
                              
                              {/* Show different buttons based on active status */}
                              {typeof feature === 'object' && feature.is_active === false ? (
                                <>
                                  {/* Restore Button - Only for revoked features */}
                                  <button 
                                    onClick={async () => {
                                      if (window.confirm(`Restore ${features.find(f => f.value === feature.name)?.label} access for ${subject.subject_name}?`)) {
                                        try {
                                          const response = await fetch(`${API_BASE}/unified-access/grant`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({
                                              student_id: parseInt(studentAccessInfo.student_id),
                                              subject_id: parseInt(subject.subject_id), 
                                              feature_name: feature.name,
                                              start_date: new Date().toISOString().split('T')[0],
                                              end_date: null,
                                              created_by: 'admin'
                                            })
                                          });

                                          if (response.ok) {
                                            setMessage('✅ Access restored successfully!');
                                            await fetchStudentAccess(studentAccessInfo.student_id);
                                          } else {
                                            setMessage('❌ Failed to restore access');
                                          }
                                        } catch (error) {
                                          console.error('Restore error:', error);
                                          setMessage('❌ Network error while restoring access');
                                        }
                                      }
                                    }}
                                    className="restore-btn"
                                    title="Restore access"
                                  >
                                    ↩️ Restore
                                  </button>
                                </>
                              ) : (
                                <>
                                  {/* Revoke Button - Only for active features */}
                                  <button 
                                    onClick={async () => {
                                      const featureName = typeof feature === 'object' ? feature.name : feature;
                                      if (window.confirm(`Revoke ${features.find(f => f.value === featureName)?.label} access for ${subject.subject_name}?\n\nThis will disable access but keep the record for audit purposes.`)) {
                                        try {
                                          const response = await fetch(`${API_BASE}/unified-access/revoke-by-criteria`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({
                                              student_id: parseInt(studentAccessInfo.student_id),
                                              subject_id: parseInt(subject.subject_id),
                                              feature_name: featureName
                                            })
                                          });

                                          if (response.ok) {
                                            setMessage('✅ Access revoked successfully!');
                                            await fetchStudentAccess(studentAccessInfo.student_id);
                                          } else {
                                            setMessage('❌ Failed to revoke access');
                                          }
                                        } catch (error) {
                                          console.error('Revoke error:', error);
                                          setMessage('❌ Network error while revoking access');
                                        }
                                      }
                                    }}
                                    className="revoke-btn"
                                    title="Revoke access (keeps record for audit)"
                                  >
                                    ⏸️ Revoke
                                  </button>
                                </>
                              )}

                              {/* Delete Button - Always available */}
                              <button 
                                onClick={async () => {
                                  const featureName = typeof feature === 'object' ? feature.name : feature;
                                  if (window.confirm(`⚠️ PERMANENTLY DELETE ${features.find(f => f.value === featureName)?.label} access for ${subject.subject_name}?\n\n⚠️ WARNING: This cannot be undone and will remove all audit history!\n\nOnly use this for correcting wrong entries.`)) {
                                    try {
                                      const requestData = {
                                        student_id: studentAccessInfo.student_id,
                                        subject_id: subject.subject_id,
                                        feature_name: featureName
                                      };

                                      console.log('Delete request data:', requestData);
                                      console.log('studentAccessInfo:', studentAccessInfo);
                                      console.log('subject:', subject);
                                      console.log('featureName:', featureName);
                                      
                                      
                                      const response = await fetch(`${API_BASE}/unified-access/delete-by-criteria`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          student_id: parseInt(studentAccessInfo.student_id),  // Convert to integer
                                          subject_id: parseInt(subject.subject_id),            // Convert to integer
                                          feature_name: featureName
                                        })
                                      });

                                      console.log('Response status:', response.status);
                                      const responseData = await response.json();
                                      console.log('Response data:', responseData);

                                      if (response.ok) {
                                        setMessage('✅ Access deleted permanently!');
                                        await fetchStudentAccess(studentAccessInfo.student_id);
                                      } else {
                                        setMessage('❌ Failed to delete access');
                                      }
                                    } catch (error) {
                                      console.error('Delete error:', error);
                                      setMessage('❌ Network error while deleting access');
                                    }
                                  }
                                }}
                                className="delete-btn"
                                title="⚠️ PERMANENTLY delete access record (cannot be undone)"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-access">
                  <p>⚠️ This student has no access to any subjects or features.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AccessControlManagement;